import React, { useState, useEffect, useRef } from "react";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { ListSubheader } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { getPopupGraphDataBySku, setState } from "./quotaModificationSlice";

const MultiSelectProductsQuotaModification = (props) => {
  const dispatch = useDispatch();
  const graphModalScenarioId = useSelector(
    (store) => store.quotaModification.graphModalScenarioId
  );
  const skuPgs = props?.skuPgs ?? [];
  let products = skuPgs.map((item) => item.packGroupOrSKUName);
  products = products.filter((item) => item !== "All");
  const [productName, setproductName] = useState([]);
  const [displayItem, setDisplayItem] = React.useState(
    Math.max(0, products.length - 1)
  );
  const [searchText, setSearchText] = React.useState("");
  const [namesData, setNamesData] = React.useState(products);
  const textInput = React.useRef(null);
  const productNameRef = useRef(productName);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    productNameRef.current = productName;
  }, [productName]);

  const getProductIdsFromNames = (productNames) => {
    const productIds = productNames.map((name) => {
      const product = skuPgs.find(
        (option) => option.packGroupOrSKUName === name
      );
      return product ? product.packGroupOrSKUID : null;
    });
    return productIds.filter((id) => id !== null);
  };

  const requestGraphBySelectedSkus = (names) => {
    if (graphModalScenarioId == null) return;
    dispatch(
      getPopupGraphDataBySku({
        scenarioID: graphModalScenarioId,
        productIds: getProductIdsFromNames(names),
      })
    );
  };

  const handleChange = (e, name) => {
    let items = [...productName];

    if (e.target.checked) items.push(name);
    else {
      var index = items.indexOf(name);
      items.splice(index, 1);
    }
    productNameRef.current = items;
    setproductName(items);
    let itemcount = items.length;
    setDisplayItem(itemcount);
    if (itemcount == 0) {
      setSelectAll(false);
    }
    if (itemcount == products.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  };

  const handleSkuDropdownClose = () => {
    requestGraphBySelectedSkus(productNameRef.current);
  };

  useEffect(() => {
    setNamesData(products);
    setSearchText("");

    if (skuPgs.length > 0) {
      setSelectAll(true);
      productNameRef.current = products;
      setproductName(products);
      setDisplayItem(products.length - 1);
    } else {
      setSelectAll(false);
      productNameRef.current = [];
      setproductName([]);
      setDisplayItem(0);
    }
  }, [skuPgs]);

  const handleDelete = (e) => {
    e.preventDefault();
    productNameRef.current = [];
    setproductName([]);
    setSelectAll(false);
    requestGraphBySelectedSkus([]);
  };

  const handleInputChange = (value) => {
    setSearchText(value);
  };

  const handleSelectAll = () => {
    let newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    if (newSelectAll) {
      let selectedItems = searchText
        ? [
            ...new Set([
              ...productName,
              ...products.filter((name) =>
                name.toLowerCase().includes(searchText.toLowerCase())
              ),
            ]),
          ]
        : products;

      productNameRef.current = selectedItems;
      setproductName(selectedItems);
      setDisplayItem(selectedItems.length - 1);
    } else {
      productNameRef.current = [];
      setproductName([]);
      setDisplayItem(0);
    }
  };

  useEffect(() => {
    let itemcount = productName.length - 1;
    setDisplayItem(itemcount);
  }, [productName]);

  // Keep list in sync with SKU data. When search is empty, update immediately so a
  // stale debounced callback cannot clear the list after skuPgs loads (race on open).
  // Depends on skuPgs (not a per-render products array) to avoid resetting debounce every render.
  useEffect(() => {
    const names = skuPgs
      .map((item) => item.packGroupOrSKUName)
      .filter((item) => item !== "All");
    const q = searchText.trim();
    if (!q) {
      setNamesData(names);
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setNamesData(
        names.filter((name) =>
          name.toLowerCase().includes(q.toLowerCase())
        )
      );
      setTimeout(() => {
        if (textInput.current) textInput.current.focus();
      }, 100);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchText, skuPgs]);

  useEffect(() => {
    if (productName.length > 0) {
      dispatch(
        setState(
          "graphModalSelectedProductIds",
          getProductIdsFromNames(productName)
        )
      );
    } else {
      dispatch(setState("graphModalSelectedProductIds", []));
    }
  }, [productName, skuPgs, dispatch]);

  return (
    <div>
      <FormControl size="small">
        <InputLabel id="quota-modification-graph-sku-pg-label">SKU / PG</InputLabel>
        <Select
          labelId="quota-modification-graph-sku-pg-label"
          id="quota-modification-graph-sku-pg"
          multiple
          label="SKU / PG"
          onClose={handleSkuDropdownClose}
          sx={{
            width: "250px !important",
            minHeight: "40px",
          }}
          value={productName}
          input={<OutlinedInput label="SKU / PG" />}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              <div>
                {productName.length === 1 && (
                  <span>{productName[0]?.substring(0, 15)}</span>
                )}
                {productName.length > 1 && (
                  <span>
                    {productName[0]?.substring(0, 15)} +{" "}
                    <b>{displayItem}</b>
                  </span>
                )}
                <span>
                  <CancelIcon
                    sx={{
                      paddingLeft: "25px",
                      top: "20%",
                      position: "absolute",
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={(e) => handleDelete(e)}
                  />
                </span>
              </div>
            </Box>
          )}
        >
          <MenuItem onClick={handleSelectAll}>
            <Checkbox checked={selectAll} />
            <ListItemText primary="Select All" />
          </MenuItem>
          <ListSubheader>
            <TextField
              size="small"
              inputRef={textInput}
              value={searchText}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchText("")}>
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Escape") {
                  e.stopPropagation();
                }
              }}
            />
          </ListSubheader>
          {namesData?.map((name, index) => (
            <MenuItem key={index + name} value={name}>
              {selectAll ? (
                <Checkbox
                  onChange={(e) => handleChange(e, name)}
                  checked={productName.some((o) => o === name)}
                />
              ) : (
                <Checkbox
                  onChange={(e) => handleChange(e, name)}
                  checked={productName.indexOf(name) > -1}
                />
              )}
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

export default MultiSelectProductsQuotaModification;
